import { useState } from 'react';

import { Button } from '../../shared/Button';
import { POST_TEMPLATES, TEMPLATE_DISPLAY_NAMES } from '../model/PostTemplates';
import { PostData } from '../model/PostTypes';
import { usePortalPostEditor } from '../Post';
import { PostTemplateSelector } from './PostTemplateSelector';
import { TemplateId, UserRoles } from '@lifeserver/shared';

import { useIsAuthorized } from '../../hooks/useIsAuthorized';

import styles from './PostTemplateInput.module.scss';

export interface PostTemplateInputProps {
    templateId: TemplateId;
    onTemplateIdChange?: (templateId: TemplateId) => void;
    value: PostData;
    onChange?: (post: PostData) => void;
}

export const PostTemplateInput = ({
    templateId,
    value,
    onChange,
    onTemplateIdChange,
}: PostTemplateInputProps) => {
    const { isEditingExistingPost } = usePortalPostEditor();
    const [isOpen, setIsOpen] = useState(!isEditingExistingPost);
    const [isClosing, setIsClosing] = useState(false);
    const template = POST_TEMPLATES[templateId];

    const authorization = useIsAuthorized(UserRoles.PortalWritePost);

    const TemplateInput = template.inputComponent;

    const selectedTemplate = templateId
        ? TEMPLATE_DISPLAY_NAMES[templateId]
        : 'Choose Post Type';

    const handleToggle = () => {
        if (isOpen) {
            // Start closing animation
            setIsClosing(true);
            setTimeout(() => {
                setIsClosing(false);
                setIsOpen(false);
            }, 300); // Match animation duration
        } else {
            // Start opening
            setIsOpen(true);
        }
    };

    return (
        <form className={styles.container} onSubmit={(e) => e.preventDefault()}>
            <fieldset disabled={!authorization.isAuthorized}>
                <legend>Post Type</legend>

                <div style={{ width: '100%' }}>
                    <Button
                        type="button"
                        onClick={handleToggle}
                        style={{
                            width: '100%',
                            textAlign: 'left',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: isOpen || isClosing ? '16px' : '0',
                            backgroundColor: templateId ? '#D8D4EC' : undefined,
                            borderColor: templateId ? '#7C70C0' : undefined,
                            color: templateId ? '#4A4A4A' : undefined,
                        }}
                    >
                        {selectedTemplate}
                        <span>{isOpen && !isClosing ? '▲' : '▼'}</span>
                    </Button>

                    {(isOpen || isClosing) && (
                        <div
                            className={
                                isClosing
                                    ? styles.accordionContentClosing
                                    : styles.accordionContent
                            }
                        >
                            <PostTemplateSelector
                                value={templateId}
                                onChange={(templateId) => {
                                    handleToggle();
                                    onTemplateIdChange?.(templateId);
                                }}
                            />
                        </div>
                    )}
                </div>
            </fieldset>
            {TemplateInput && (
                <fieldset disabled={!authorization.isAuthorized}>
                    <legend>Post Content</legend>

                    <TemplateInput value={value} onChange={onChange} />
                </fieldset>
            )}
        </form>
    );
};
